type LivewrappedCommand = () => void;

interface LivewrappedBootstrap {
  loaded?: false;
  load?: (ad: Element) => void;
  cmd?: LivewrappedCommand[];
}

interface LivewrappedReady {
  loaded: true;
  load: (ad: Element) => void;
  cmd: { push: (command: LivewrappedCommand) => unknown };
}

declare global {
  interface Window {
    lwhb: LivewrappedBootstrap | LivewrappedReady | undefined;
  }
}

function handleLoadAdInDevelopment(el: HTMLElement) {
  console.log('DEVELOPMENT: loading FAKE ad: ', el.dataset.tid);
  const txsParent = el.parentElement;
  if (txsParent?.classList.contains('txs-a')) {
    el.innerText = el.attributes.getNamedItem('data-tid')?.value ?? 'unknown';
  }
}

function scheduleAdLoad(el: HTMLElement): void {
  if (process.env.NODE_ENV === 'development') {
    handleLoadAdInDevelopment(el);
    return;
  }

  const livewrapped = window.lwhb ?? {};
  window.lwhb = livewrapped;

  if (livewrapped.loaded) {
    livewrapped.load(el);
    return;
  }

  const commandQueue = Array.isArray(livewrapped.cmd) ? livewrapped.cmd : [];
  livewrapped.cmd = commandQueue;
  commandQueue.push(() => window.lwhb?.load?.(el));
}

function isFullpageAd(el: HTMLElement): boolean {
  return el.dataset.tid?.includes('helsida') ?? false;
}

// Matches the CSS breakpoint: mb-only ends at 768px, dt-only starts at 769px.
const DESKTOP_BREAKPOINT = 769;

function matchesViewport(el: HTMLElement): boolean {
  const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
  if (el.classList.contains('mb') && isDesktop) return false;
  if (el.classList.contains('dt') && !isDesktop) return false;
  return true;
}

function createLeeAdObserver() {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (
            entry.target instanceof HTMLElement &&
            entry.target?.dataset.custom === 'true' &&
            entry.target?.dataset.pbid
          ) {
            scheduleAdLoad(entry.target);
            observer.unobserve(entry.target);
          }
        }
      });
    },
    {
      root: null,
      rootMargin: '600px',
    },
  );
  return {
    add: (nodes: HTMLElement | HTMLElement[] | NodeListOf<HTMLElement>) => {
      const ads = Array.isArray(nodes) || nodes instanceof NodeList ? [...nodes] : [nodes];

      ads.forEach((ad) => {
        if (ad.dataset.custom === 'true') {
          // Fullpage ads have display:none so IntersectionObserver will never fire — load immediately.
          // matchesViewport prevents loading e.g. mobile fullpage on desktop.
          // Regular ads don't need this check — CSS hides them and the observer never fires.
          if (isFullpageAd(ad)) {
            if (matchesViewport(ad)) scheduleAdLoad(ad);
          } else {
            observer.observe(ad);
          }
        }
      });
    },
  };
}

export default createLeeAdObserver;
